import { Container, Graphics, TilingSprite, Texture } from 'pixi.js';
import { assetLoader } from '../core/AssetLoader';

// Texture keys for baked star layers
const STAR_LAYER_KEYS = ['starfield-far', 'starfield-mid', 'starfield-near'];

// Static flag to track if textures have been generated
let texturesGenerated = false;
const starTextures: Map<string, Texture> = new Map();

// Simple mulberry32 PRNG - much better distribution than LCG
function mulberry32(seed: number) {
  return function() {
    let t = seed += 0x6D2B79F5;
    t = Math.imul(t ^ t >>> 15, t | 1);
    t ^= t + Math.imul(t ^ t >>> 7, t | 61);
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };
}

export class Starfield extends Container {
  private starLayers: TilingSprite[] = [];
  private blurGraphics: Graphics; // For speed blur effect
  private lastCameraX: number = 0;
  private lastCameraY: number = 0;
  private lastDeltaX: number = 0;
  private lastDeltaY: number = 0;
  private readonly LAYER_SIZE = 512; // Texture size for tiling

  // Parallax factors for each layer (far, mid, near)
  private readonly PARALLAX_FACTORS = [0.15, 0.25, 0.4];

  // Canvas dimensions for positioning
  private canvasWidth: number;
  private canvasHeight: number;

  // Callback for resize cleanup
  private resizeHandler: () => void;

  constructor(canvasWidth: number, canvasHeight: number) {
    super();

    this.canvasWidth = canvasWidth;
    this.canvasHeight = canvasHeight;

    // Generate textures once per game session
    this.ensureTexturesExist();

    // Create blur graphics layer (drawn on top of stars when moving fast)
    this.blurGraphics = new Graphics();
    this.blurGraphics.zIndex = 10;
    this.addChild(this.blurGraphics);

    this.zIndex = -45;

    // Setup resize handler
    this.resizeHandler = () => this.handleScaleResize();
    window.addEventListener('resize', this.resizeHandler);

    // Create star layers
    this.createStarLayers();
  }

  private handleScaleResize(): void {
    const canvasWidth = window.innerWidth;
    const canvasHeight = window.innerHeight;
    this.resize(canvasWidth, canvasHeight);
  }

  private createStarLayers(): void {
    // Starfield layers with different depths
    const starDepths = [-3, -2, -1];

    for (let i = 0; i < 3; i++) {
      const texture = starTextures.get(STAR_LAYER_KEYS[i])!;
      const tileSprite = new TilingSprite({
        texture,
        width: this.canvasWidth,
        height: this.canvasHeight,
      });
      // No anchor - position at 0,0 (fills screen space)
      tileSprite.position.set(0, 0);
      tileSprite.zIndex = starDepths[i];
      this.addChild(tileSprite);
      this.starLayers.push(tileSprite);
    }

    // Enable sorting
    this.sortableChildren = true;
  }

  private ensureTexturesExist(): void {
    if (texturesGenerated) return;

    // Generate three star layer textures with different densities and star sizes
    this.generateStarLayerTexture(STAR_LAYER_KEYS[0], 40, 0.5, 1.0, 0.2, 12345); // Far: sparse tiny dim stars
    this.generateStarLayerTexture(STAR_LAYER_KEYS[1], 20, 0.8, 1.2, 0.4, 67890);  // Mid: few small stars
    this.generateStarLayerTexture(STAR_LAYER_KEYS[2], 10, 1.0, 1.5, 0.6, 13579);  // Near: rare medium bright stars

    texturesGenerated = true;
  }

  private generateStarLayerTexture(
    key: string,
    starCount: number,
    minSize: number,
    maxSize: number,
    baseAlpha: number,
    seed: number
  ): void {
    const graphics = new Graphics();
    const size = this.LAYER_SIZE;

    // Use mulberry32 PRNG for good random distribution
    const random = mulberry32(seed);

    for (let i = 0; i < starCount; i++) {
      // Get random values with proper distribution
      const x = random() * size;
      const y = random() * size;
      const sizeRand = random();
      const brightnessRand = random();

      const starSize = minSize + sizeRand * (maxSize - minSize);
      const brightness = baseAlpha + brightnessRand * 0.3;

      // Draw star point as filled circle
      graphics.circle(x, y, starSize);
      graphics.fill({ color: 0xffffff, alpha: brightness });

      // Add subtle glow for brighter stars
      if (brightness > 0.6) {
        graphics.circle(x, y, starSize * 2);
        graphics.fill({ color: 0xffffff, alpha: brightness * 0.3 });
      }
    }

    const texture = assetLoader.generateTexture(graphics, key);
    starTextures.set(key, texture);
    graphics.destroy();
  }

  update(cameraX: number, cameraY: number, playerSpeed: number, cameraZoom: number = 1, isBoosting: boolean = false): void {
    // Calculate camera delta for blur effect
    const deltaCameraX = cameraX - this.lastCameraX;
    const deltaCameraY = cameraY - this.lastCameraY;

    this.lastCameraX = cameraX;
    this.lastCameraY = cameraY;

    // Smooth the delta for blur effect
    this.lastDeltaX = this.lastDeltaX * 0.8 + deltaCameraX * 0.2;
    this.lastDeltaY = this.lastDeltaY * 0.8 + deltaCameraY * 0.2;

    // Fade out star layers when zoomed out to reduce visible tiling pattern
    const ZOOM_FADE_START = 0.5;
    const ZOOM_MIN = 0.125;
    const zoomFactor = Math.max(0, Math.min(1, (cameraZoom - ZOOM_MIN) / (ZOOM_FADE_START - ZOOM_MIN)));

    // Base alphas for each layer when zoomed in, fading to lower values when zoomed out
    const baseAlphas = [0.4, 0.6, 0.8];  // far, mid, near base alpha at full zoom
    const minAlphas = [0.15, 0.1, 0.0];  // far, mid, near min alpha when zoomed out

    // Scroll each tile layer at different parallax rates via tilePosition
    for (let i = 0; i < this.starLayers.length; i++) {
      const layer = this.starLayers[i];
      const parallax = this.PARALLAX_FACTORS[i];

      // Parallax via tilePosition - screen stays fixed, only tile offset changes
      layer.tilePosition.x = -cameraX * parallax;
      layer.tilePosition.y = -cameraY * parallax;

      // Apply zoom-based alpha fading
      const targetAlpha = minAlphas[i] + (baseAlphas[i] - minAlphas[i]) * zoomFactor;
      layer.alpha = targetAlpha;
    }

    // Draw motion blur effect when moving fast or boosting (in screen space)
    this.drawSpeedBlur(playerSpeed, this.canvasWidth, this.canvasHeight, isBoosting);
  }

  private drawSpeedBlur(playerSpeed: number, width: number, height: number, isBoosting: boolean): void {
    this.blurGraphics.clear();

    // Calculate blur factor from speed (threshold: 150, max effect at 400)
    let blurFactor = Math.max(0, Math.min((playerSpeed - 150) / 250, 1));

    // Boosting adds extra blur effect - lower threshold and higher intensity
    if (isBoosting) {
      // When boosting, start showing effect at lower speed and enhance it
      const boostBlur = Math.max(0, Math.min((playerSpeed - 50) / 150, 1));
      blurFactor = Math.max(blurFactor, boostBlur * 0.8); // Boost gives up to 80% blur
    }

    if (blurFactor <= 0) return;

    // Calculate blur direction from camera movement
    const moveLength = Math.sqrt(this.lastDeltaX * this.lastDeltaX + this.lastDeltaY * this.lastDeltaY);
    if (moveLength < 0.5) return;

    const moveAngle = Math.atan2(this.lastDeltaY, this.lastDeltaX);

    // Draw speed lines parallel to movement direction
    // More lines and longer when boosting
    const baseLines = isBoosting ? 20 : 15;
    const maxLines = isBoosting ? 40 : 25;
    const numLines = Math.floor(baseLines + blurFactor * (maxLines - baseLines));
    const maxLineLength = isBoosting ? (80 + blurFactor * 200) : (50 + blurFactor * 150);

    // Use a fixed seed PRNG for consistent line positions
    const random = mulberry32(42);

    for (let i = 0; i < numLines; i++) {
      const rand1 = random();
      const rand2 = random();
      const rand3 = random();

      // Position spread across the screen
      const spreadX = width / 2 + (rand1 - 0.5) * width * 1.5;
      const spreadY = height / 2 + (rand2 - 0.5) * height * 1.5;

      const lineLength = (0.3 + rand3 * 0.7) * maxLineLength * blurFactor;

      // Slightly higher alpha when boosting for more visible effect
      const baseAlpha = isBoosting ? 0.15 : 0.1;
      const maxAlpha = isBoosting ? 0.35 : 0.2;
      const alpha = (baseAlpha + rand3 * (maxAlpha - baseAlpha)) * blurFactor;

      // Lines extend opposite to movement direction (trailing behind)
      const startX = spreadX;
      const startY = spreadY;
      const endX = startX + Math.cos(moveAngle + Math.PI) * lineLength;
      const endY = startY + Math.sin(moveAngle + Math.PI) * lineLength;

      // Line color: white normally, slight cyan tint when boosting
      const color = isBoosting ? 0xccffff : 0xffffff;

      this.blurGraphics.moveTo(startX, startY);
      this.blurGraphics.lineTo(endX, endY);
      this.blurGraphics.stroke({ width: 1 + rand3, color, alpha });
    }
  }

  // Resize layers when canvas size changes
  resize(canvasWidth: number, canvasHeight: number): void {
    this.canvasWidth = canvasWidth;
    this.canvasHeight = canvasHeight;

    for (const layer of this.starLayers) {
      layer.width = canvasWidth;
      layer.height = canvasHeight;
    }
  }

  destroy(): void {
    window.removeEventListener('resize', this.resizeHandler);
    for (const layer of this.starLayers) {
      layer.destroy();
    }
    this.blurGraphics.destroy();
    this.starLayers = [];
    super.destroy();
  }
}
